import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { GitBranch, CheckCircle2, XCircle, SlidersHorizontal, ShieldCheck } from "lucide-react";

const TIER_TONE = { A: "success", B: "warning", C: "outline", D: "destructive" };
const PERF = { low: "success", moderate: "warning", high: "destructive", unknown: "outline" };
const STATUS_TONE = { accepted: "success", adjusted: "success", awaiting_ghg_approval: "warning", rejected: "destructive" };

function Stat({ label, value, sub }) {
  return <div className="card-flat p-4"><div className="label-eyebrow text-[10px] mb-1">{label}</div><div className="font-display text-3xl font-bold">{value}</div>{sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}</div>;
}

export default function Scope3Bridge() {
  const [sum, setSum] = useState(null);
  const [recs, setRecs] = useState([]);
  const [meth, setMeth] = useState(null);

  const load = () => {
    api.get("/scope3-bridge/summary").then((r) => setSum(r.data)).catch(() => setSum({ empty: true }));
    api.get("/scope3-bridge/activity-records").then((r) => { setRecs(r.data.records || []); setMeth(r.data.methodology); });
  };
  useEffect(() => { load(); }, []);

  const review = async (rid, action) => {
    try { await api.post(`/scope3-bridge/activity-records/${rid}/review`, { action, reviewer: "ghg.lead@tropigarden.demo", note: `${action} via GHG review` }); toast.success(`Record ${action}ed`); load(); }
    catch (e) { toast.error("Review failed"); }
  };

  if (!sum) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading Scope 3 Data Bridge…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="scope3-bridge-page">
      <div>
        <div className="label-eyebrow mb-1">GHG Accounting · Scope 3 Data Manager</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3"><GitBranch className="h-7 w-7 text-accent" /> Scope 3 Data Bridge</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">Supplier facts become draft <span className="text-foreground">Scope 3 Activity Records</span> that the GHG engine reviews and accepts before they count as official emissions. One supplier truth, one authoritative GHG ledger.</p>
      </div>

      {!sum.empty && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Accepted (Cat 1)" value={sum.accepted_emissions_tco2e} sub="tCO₂e in inventory" />
            <Stat label="Awaiting approval" value={sum.awaiting_approval} sub="draft records" />
            <Stat label="Data quality score" value={sum.data_quality_score} sub="Lestari indicator" />
            <Stat label="Primary data" value={`${sum.primary_data_pct}%`} />
            <Stat label="Verified" value={`${sum.verified_pct}%`} />
            <Stat label="CBAM lines" value={sum.cbam_relevant_lines} sub="separate domain" />
          </div>
          <div className="card-flat p-3 text-[11px] text-muted-foreground flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> {meth?.name} v{meth?.version} · {meth?.factor_library} · {meth?.gwp_basis} — stamped on every calculation. {sum.cbam_note}</div>
        </>
      )}

      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3">Scope 3 Activity Records · A→D calculation hierarchy</div>
        <div className="space-y-2">
          {recs.map((r) => (
            <div key={r.id} className="p-3 rounded-sm border border-border bg-surface" data-testid={`act-${r.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-medium">{r.product_name} <span className="text-muted-foreground font-normal">· {r.supplier_name}</span></div>
                  <div className="text-[10px] font-mono text-muted-foreground">{r.suggested_category}</div>
                </div>
                <Badge variant={TIER_TONE[r.method_tier]} className="text-[9px]" title={r.method_label}>Method {r.method_tier}</Badge>
                <Badge variant={PERF[r.carbon_performance.rating]} className="text-[8px] uppercase">perf {r.carbon_performance.rating}</Badge>
                <span className="font-mono text-[10px]" title="Data confidence">DQ {r.data_quality.score}</span>
                <div className="text-right w-20"><div className="font-mono text-sm font-bold">{r.co2e_tonnes ?? "—"}</div><div className="text-[9px] text-muted-foreground">tCO₂e</div></div>
                <Badge variant={STATUS_TONE[r.status]} className="text-[8px] uppercase">{r.status.replace(/_/g, " ")}</Badge>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1.5">{r.method_reason}</div>
              {r.method_tier !== "A" && <div className="text-[10px] text-accent mt-0.5">↑ {r.improvement_roadmap}</div>}
              {r.cbam_relevant && <div className="text-[10px] text-[#FFB800] mt-0.5">⚠ {r.cbam_note}</div>}
              {r.review && <div className="text-[10px] text-muted-foreground mt-1">Reviewed by {r.review.by} · accepted as {r.review.accepted_category} (Method {r.review.accepted_method_tier})</div>}
              {r.status === "awaiting_ghg_approval" && (
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="primary" onClick={() => review(r.id, "accept")} data-testid={`accept-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Accept</Button>
                  <Button size="sm" variant="outline" onClick={() => review(r.id, "adjust")} data-testid={`adjust-${r.id}`}><SlidersHorizontal className="h-3.5 w-3.5" /> Adjust</Button>
                  <Button size="sm" variant="ghost" onClick={() => review(r.id, "reject")} data-testid={`reject-${r.id}`}><XCircle className="h-3.5 w-3.5" /> Reject</Button>
                </div>
              )}
            </div>
          ))}
          {!recs.length && <div className="text-center text-muted-foreground text-sm py-8">No activity records yet. Capture supplier products in Products & Traceability.</div>}
        </div>
      </div>
    </div>
  );
}
